import { ChangeEvent, FC, useRef, useState, DragEvent, useEffect, useMemo } from 'react';
import cx from 'classnames';
import UploadSingle from 'components/Icons/UploadSingle';
import { compressImageFile } from 'utils/file.utils';
import { showErrorToast } from 'utils/toast.util';
import UIButton from './UIButton';
import { useProductStore } from 'zus/productStore';

interface UIFileDropzoneProps {
  allowedFileExtensions: string[];
  // in some operation systems accept is not working properly file select dialog extension filter can be toggled, we want allowed extensions for manual check onFileSelect event.
  accept: string;
  maximumFileSizeMB: number;
  // eslint-disable-next-line no-unused-vars
  onFileSelected: (file: File) => void;
  resizeImages?: boolean;
  buttonText?: string;
  description?: string | JSX.Element;
  cancelFileSelected?: any;
}

const UIFileDropzoneV2: FC<UIFileDropzoneProps> = ({
  allowedFileExtensions,
  maximumFileSizeMB,
  onFileSelected,
  resizeImages,
  description,
  accept,
  buttonText,
  cancelFileSelected
}) => {
  const [isCompressionLoading, setIsCompressionLoading] = useState(false);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const uploadFormFileSelected = useProductStore((state:any) => state.uploadFormFileSelected);
  const setUploadFormFileSelected = useProductStore((state:any) => state.setUploadFormFileSelected);

  const onInputChangeHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    console.log("input change");
    const [file] = event.target.files || [];
    
    fileSelectedHandler(file);
    
  };

  hiddenInputRef.current?.addEventListener('cancel', cancelFileSelected);

  const fileSelectedHandler = async (file: File) => {
    
    if (!file || isCompressionLoading) return;

    const { valid, message } = validateFile(file);
    if (!valid) {
      showErrorToast(message as string);
      resetFileInput();
      return;
    }
    if (resizeImages) {
      setIsCompressionLoading(true);
      try {
        file = await compressImageFile(file, {
          maxWidth: 1920,
          maxHeight: 1080,
          minWidth: 640,
          minHeight: 480,
          quality: 65
        });
      } finally {
        setIsCompressionLoading(false);
      }
    }
    onFileSelected(file);
    setUploadFormFileSelected(true);
    resetFileInput();
  };

  const resetFileInput = () => {
    if (!hiddenInputRef.current) return;

    hiddenInputRef.current.value = '';
  };

  useEffect(() => {
    toggleFileSelectDialog();
  },[]);

  const validateFile = (file: File): { valid: boolean; message?: string } => {
    const genericValidationErrorResult = {
      valid: false,
      message: 'File is not valid!'
    };

    if (!file) return genericValidationErrorResult;

    const fileSizeInMB = file.size / 1000000;
    if (fileSizeInMB > maximumFileSizeMB) {
      return {
        valid: false,
        message: `The file size must be less than ${maximumFileSizeMB}MB.`
      };
    }

    const fileExtension = file.name.split('.').pop();
    if (!fileExtension) return genericValidationErrorResult;

    const isFileExtensionValid = allowedFileExtensions
      .map((ext) => ext.toLowerCase())
      .includes(fileExtension?.toLowerCase());

    if (!isFileExtensionValid) {
      return {
        valid: false,
        message: `.${fileExtension.toUpperCase()} is not an allowed file extension.`
      };
    }

    return {
      valid: true
    };
  };

  const toggleFileSelectDialog = () => {
    if (!hiddenInputRef.current || isCompressionLoading) return;

    hiddenInputRef.current.click();
  };


  const [isFileDropActive, setIsFileDropActive] = useState(false);

  const onDragDropEnterHandler = (event: DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const isContainsFiles = !!event.dataTransfer.types.find(
      (dtType) => dtType === 'Files'
    );
    setIsFileDropActive(isContainsFiles);
  };
  const onDragDropLeaveHandler = (event: DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setIsFileDropActive(false);
  };
  const onDropHandler = (event: DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setIsFileDropActive(false);

    const fileDataTransferItem = [...event.dataTransfer.items].find(
      (item) => item.kind === 'file'
    );

    if (!fileDataTransferItem) return;

    if (event.dataTransfer.items.length > 1) {
      showErrorToast('Please drop one file.');
      return;
    }

    const droppedFile = fileDataTransferItem.getAsFile();

    if (!droppedFile) return;

    fileSelectedHandler(droppedFile);
  };
  return (
    <div className="relative flex flex-col">
      <input
        type="file"
        className="hidden"
        ref={hiddenInputRef}
        accept={accept}
        onChange={onInputChangeHandler}
      />
      <h2 className='text-center font-semibold text-xl mb-5'>Upload prescription</h2>
      {!!description && (
        <div className="mb-[20px] text-[14px] text-center text-brand-grey300">
          {/* {description} */}
          {<>After your prescription is uploaded, please enter your PD number.</>}
        </div>
      )}
      <div
        data-dresden-dropzone
        onClick={toggleFileSelectDialog}
        onDrop={onDropHandler}
        onDragEnter={onDragDropEnterHandler}
        onDragOver={onDragDropEnterHandler}
        onDragLeave={onDragDropLeaveHandler}
        onDragEnd={onDragDropLeaveHandler}
        className={cx(
          'display flex cursor-pointer min-h-[100px] bg-brand-grey650 w-full select-none flex-col items-center justify-center rounded-[6px] border-brand-grey350 transition-all',
          {
            'border-brand-blue': isFileDropActive,
            'border-brand-grey350': !isFileDropActive
          }
        )}>
        {isFileDropActive ? (
          <div className="flex flex-col items-center justify-center text-brand-blue">
            <UploadSingle fill="currentColor" width={46} height={46} />
            <span className="mt-[20px] text-[14px] font-medium">
              You can drop the file here.
            </span>
          </div>
        ) : (
          <div className='flex text-brand-blue25'>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M11.25 13.9499V17.6346C11.25 17.8474 11.3218 18.0256 11.4654 18.1692C11.609 18.3128 11.7872 18.3846 12 18.3846C12.2128 18.3846 12.391 18.3128 12.5346 18.1692C12.6782 18.0256 12.7499 17.8474 12.7499 17.6346V13.9499L14.0731 15.273C14.1474 15.3474 14.2311 15.4031 14.324 15.4403C14.417 15.4775 14.5099 15.4935 14.6028 15.4884C14.6958 15.4833 14.7878 15.4621 14.8788 15.425C14.9698 15.3878 15.0525 15.332 15.1269 15.2576C15.2718 15.1025 15.3468 14.9269 15.3519 14.7307C15.357 14.5346 15.282 14.3589 15.1269 14.2038L12.6327 11.7096C12.5391 11.616 12.4404 11.55 12.3365 11.5115C12.2327 11.4731 12.1205 11.4538 12 11.4538C11.8795 11.4538 11.7673 11.4731 11.6634 11.5115C11.5596 11.55 11.4609 11.616 11.3673 11.7096L8.87308 14.2038C8.72436 14.3525 8.65096 14.5266 8.65287 14.7259C8.65479 14.9253 8.73332 15.1025 8.88845 15.2576C9.04357 15.4025 9.2192 15.4775 9.41535 15.4826C9.6115 15.4878 9.78714 15.4128 9.94227 15.2576L11.25 13.9499ZM6.3077 21.5C5.80257 21.5 5.375 21.325 5.025 20.975C4.675 20.625 4.5 20.1974 4.5 19.6922V4.3077C4.5 3.80257 4.675 3.375 5.025 3.025C5.375 2.675 5.80257 2.5 6.3077 2.5H13.5019C13.7429 2.5 13.9747 2.5468 14.1971 2.6404C14.4195 2.73398 14.6128 2.86282 14.7769 3.02692L18.973 7.22303C19.1371 7.38714 19.266 7.58042 19.3595 7.80285C19.4531 8.02528 19.5 8.25701 19.5 8.49803V19.6922C19.5 20.1974 19.325 20.625 18.975 20.975C18.625 21.325 18.1974 21.5 17.6922 21.5H6.3077ZM13.5 7.5961V3.99997H6.3077C6.23077 3.99997 6.16024 4.03203 6.09612 4.09613C6.03202 4.16024 5.99997 4.23077 5.99997 4.3077V19.6922C5.99997 19.7692 6.03202 19.8397 6.09612 19.9038C6.16024 19.9679 6.23077 20 6.3077 20H17.6922C17.7692 20 17.8397 19.9679 17.9038 19.9038C17.9679 19.8397 18 19.7692 18 19.6922V8.49995H14.4039C14.1462 8.49995 13.9311 8.41373 13.7587 8.24128C13.5862 8.06884 13.5 7.85378 13.5 7.5961Z" fill="#00AEC7"/>
              </svg>
              {'Upload prescription'}
            
            {/* <p className="mt-[20px] text-[14px] font-medium text-brand-black100">
              or drag & drop file here
            </p> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default UIFileDropzoneV2;
